import { fromTheme as kyruusFromTheme } from '@kyruus/ui-theme';

/**
 * Converts a pixel value string (e.g. "16px") to a number.
 * @param {string} key - The pixel value string to convert.
 * @returns {number} The numeric value of the pixel string.
 */
const convertToNumber = (key: string): number =>
  parseInt(key.replace('px', ''), 10);

/**
 * Builds the typography configuration for the MUI theme based on Kyruus theme values.
 * @param {object} kyruusTheme - The Kyruus theme object
 * @returns {object} The typography configuration for MUI theme
 */
export const buildTypography = (kyruusTheme: Record<string, unknown>) => {
  // wrap fromTheme to simplify the code below
  const fromTheme = (themeKey: string) =>
    kyruusFromTheme(themeKey)({ theme: kyruusTheme });

  const fontFace = fromTheme('font_face');
  const fontSize = convertToNumber(fromTheme('font_size'));
  const fontSizeSmall = convertToNumber(fromTheme('font_size_small'));
  const fontSizeHeading1 = convertToNumber(fromTheme('font_size_heading_1'));
  const fontSizeHeading2 = convertToNumber(fromTheme('font_size_heading_2'));
  const fontSizeHeading3 = convertToNumber(fromTheme('font_size_heading_3'));
  const fontSizeHeading4 = convertToNumber(fromTheme('font_size_heading_4'));
  const fontSizeHeading5 = convertToNumber(fromTheme('font_size_heading_5'));
  const fontWeightLight = fromTheme('font_weight_light');
  const fontWeight = fromTheme('font_weight');
  const fontWeightBold = fromTheme('font_weight_bold');
  const fontWeightBolder = fromTheme('font_weight_bolder');
  const fontColor = fromTheme('color_text');

  return {
    fontFamily: fontFace,
    fontSize: fontSize,
    htmlFontSize: fontSize,
    fontWeightLight: fontWeightLight,
    fontWeightRegular: fontWeight,
    fontWeightMedium: fontWeightBold,
    fontWeightBold: fontWeightBolder,
    allVariants: {
      color: fontColor,
      fontFamily: fontFace
    },
    h1: {
      fontFamily: fontFace,
      fontSize: fontSizeHeading1,
      fontWeight: fontWeightBold
    },
    h2: {
      fontFamily: fontFace,
      fontSize: fontSizeHeading2,
      fontWeight: fontWeightBold
    },
    h3: {
      fontFamily: fontFace,
      fontSize: fontSizeHeading3,
      fontWeight: fontWeightBold
    },
    h4: {
      fontFamily: fontFace,
      fontSize: fontSizeHeading4,
      fontWeight: fontWeightBold
    },
    h5: {
      fontFamily: fontFace,
      fontSize: fontSizeHeading5,
      fontWeight: fontWeight
    },
    h6: { fontFamily: fontFace, fontSize: fontSize, fontWeight: fontWeight },
    subtitle1: {
      fontFamily: fontFace,
      fontSize: fontSize,
      fontWeight: fontWeight
    },
    subtitle2: {
      fontFamily: fontFace,
      fontSize: fontSize * 0.875,
      fontWeight: fontWeight
    },
    body1: { fontFamily: fontFace, fontSize: fontSize, fontWeight: fontWeight },
    body2: {
      fontFamily: fontFace,
      fontSize: fontSizeSmall,
      fontWeight: fontWeight
    },
    caption: {
      fontFamily: fontFace,
      fontSize: fontSize * 0.75,
      fontWeight: fontWeight
    },
    overline: {
      fontFamily: fontFace,
      fontSize: fontSize * 0.75,
      fontWeight: fontWeight,
      textTransform: 'uppercase'
    },
    button: {
      fontFamily: fontFace,
      fontSize: fontSize,
      fontWeight: fontWeightBold,
      textTransform: 'none'
    }
  };
};
